import { supabase } from "@/integrations/supabase/client";
import { ICD11Service, ICD11Entry } from "./icd11Service";
import { MedicalKnowledgeService, MedicalKnowledgeEntry } from "./medicalKnowledge";

export interface AdvancedDiagnosticResult {
  potentialDiagnoses: EnhancedDiagnosis[];
  clinicalReasoningPath: string[];
  evidenceQuality: 'high' | 'medium' | 'low';
  totalConditionsConsidered: number;
  databaseCoverage: {
    icd11Matches: number;
    researchPapers: number;
    clinicalGuidelines: number;
  };
}

export interface EnhancedDiagnosis {
  condition: string;
  icdCode?: string;
  probability: number;
  severity: 'mild' | 'moderate' | 'severe';
  reasoning: string;
  clinicalPearls?: string[];
  requiredTests?: string[];
  laymanDescription?: string;
  evidenceLevel: 'high' | 'medium' | 'low';
  supportingStudies: string[];
  urgencyLevel: 'routine' | 'urgent' | 'immediate';
  prevalence?: string;
  riskFactors?: string[];
}

export class AdvancedDiagnosticAnalysisService {
  /**
   * Generate comprehensive diagnostic analysis using the full medical database
   */
  static async generateIntelligentDiagnosticAnalysis(
    answers: Record<string, any>
  ): Promise<AdvancedDiagnosticResult> {
    console.log('Starting advanced diagnostic analysis with full database integration');
    
    try {
      // Extract comprehensive symptom and clinical data
      const clinicalData = this.extractClinicalData(answers);
      
      // Parallel search across all medical databases
      const [icd11Results, medicalKnowledge, symptomSpecificKnowledge] = await Promise.all([
        ICD11Service.mapResponsesToICD11({
          field: clinicalData.field,
          symptoms: clinicalData.symptoms,
          duration: clinicalData.duration,
          pattern: clinicalData.pattern,
          impact: clinicalData.dailyImpact
        }),
        MedicalKnowledgeService.searchRelevantKnowledge(
          clinicalData.symptoms,
          clinicalData.conditions,
          ['clinical_research', 'diagnosis', 'emergency_medicine'],
          50
        ),
        this.searchSymptomSpecificConditions(clinicalData.symptoms)
      ]);

      console.log('Database search results:', {
        icd11Primary: icd11Results.primaryConditions.length,
        icd11Secondary: icd11Results.secondaryConditions.length,
        medicalKnowledge: medicalKnowledge.length,
        symptomSpecific: symptomSpecificKnowledge.length
      });

      // Generate comprehensive differential diagnosis
      const potentialDiagnoses = await this.generateComprehensiveDifferential(
        clinicalData,
        icd11Results,
        medicalKnowledge,
        symptomSpecificKnowledge
      );

      // Create clinical reasoning pathway
      const clinicalReasoningPath = this.generateClinicalReasoning(
        clinicalData,
        potentialDiagnoses,
        icd11Results
      );

      // Assess evidence quality
      const evidenceQuality = this.assessEvidenceQuality(
        medicalKnowledge,
        icd11Results,
        potentialDiagnoses
      );

      const result: AdvancedDiagnosticResult = {
        potentialDiagnoses: potentialDiagnoses.slice(0, 10), // Top 10 most likely
        clinicalReasoningPath,
        evidenceQuality,
        totalConditionsConsidered: potentialDiagnoses.length,
        databaseCoverage: {
          icd11Matches: icd11Results.primaryConditions.length + icd11Results.secondaryConditions.length,
          researchPapers: medicalKnowledge.length,
          clinicalGuidelines: medicalKnowledge.filter(k => k.source === 'trip').length
        }
      };

      console.log('Advanced diagnostic analysis completed:', {
        diagnosesGenerated: result.potentialDiagnoses.length,
        totalConsidered: result.totalConditionsConsidered,
        evidenceQuality: result.evidenceQuality,
        databaseCoverage: result.databaseCoverage
      });

      return result;

    } catch (error) {
      console.error('Error in advanced diagnostic analysis:', error);
      return this.generateFallbackDiagnosis(answers);
    }
  }

  /**
   * Extract comprehensive clinical data from questionnaire
   */
  private static extractClinicalData(answers: Record<string, any>) {
    const symptoms = answers.symptom_constellation?.selections || [];
    const systemicSymptoms = Array.isArray(answers.systemic_symptoms) 
      ? answers.systemic_symptoms.filter((s: string) => s !== "None of the above")
      : [];
    
    const allSymptoms = [...symptoms, ...systemicSymptoms];
    
    return {
      symptoms: allSymptoms,
      conditions: [], // Will be populated by analysis
      category: answers.symptom_constellation?.category || '',
      duration: answers.symptom_constellation?.duration || '',
      severity: answers.symptom_constellation?.severity || 5,
      pattern: answers.symptom_constellation?.pattern || '',
      dailyImpact: parseInt(answers.daily_impact) || 5,
      emergencyScreening: answers.emergency_screening === 'Yes',
      field: this.mapCategoryToBodySystem(answers.symptom_constellation?.category)
    };
  }

  /**
   * Search for symptom-specific conditions across medical databases
   */
  private static async searchSymptomSpecificConditions(symptoms: string[]): Promise<MedicalKnowledgeEntry[]> {
    if (!symptoms.length) return [];

    try {
      // Search for each symptom individually to maximize coverage
      const symptomSearches = symptoms.map(symptom => 
        MedicalKnowledgeService.searchRelevantKnowledge(
          [symptom],
          [],
          ['clinical_research', 'diagnosis'],
          10
        )
      );

      const results = await Promise.all(symptomSearches);
      const allResults = results.flat();
      
      // Remove duplicates and return unique conditions
      const uniqueResults = allResults.reduce((acc, current) => {
        const exists = acc.find(item => item.external_id === current.external_id);
        if (!exists) {
          acc.push(current);
        }
        return acc;
      }, [] as MedicalKnowledgeEntry[]);

      return uniqueResults;
    } catch (error) {
      console.error('Error searching symptom-specific conditions:', error);
      return [];
    }
  }

  /**
   * Generate comprehensive differential diagnosis using all available data
   */
  private static async generateComprehensiveDifferential(
    clinicalData: any,
    icd11Results: any,
    medicalKnowledge: MedicalKnowledgeEntry[],
    symptomSpecificKnowledge: MedicalKnowledgeEntry[]
  ): Promise<EnhancedDiagnosis[]> {
    const diagnoses: EnhancedDiagnosis[] = [];

    // 1. Process ICD-11 primary conditions
    for (const icdEntry of icd11Results.primaryConditions) {
      const diagnosis = await this.createDiagnosisFromICD11(icdEntry, clinicalData, medicalKnowledge);
      diagnoses.push(diagnosis);
    }

    // 2. Process ICD-11 secondary conditions
    for (const icdEntry of icd11Results.secondaryConditions) {
      const diagnosis = await this.createDiagnosisFromICD11(icdEntry, clinicalData, medicalKnowledge);
      diagnosis.probability = Math.max(20, diagnosis.probability - 15); // Lower probability for secondary
      diagnoses.push(diagnosis);
    }

    // 3. Extract conditions from medical research
    const researchConditions = this.extractConditionsFromResearch(medicalKnowledge, clinicalData);
    diagnoses.push(...researchConditions);

    // 4. Extract conditions from symptom-specific research
    const symptomConditions = this.extractConditionsFromResearch(symptomSpecificKnowledge, clinicalData);
    diagnoses.push(...symptomConditions);

    // 5. Add common conditions for symptom category if coverage is low
    if (diagnoses.length < 5) {
      const commonConditions = await this.getCommonConditionsForCategory(clinicalData.category, clinicalData);
      diagnoses.push(...commonConditions);
    }

    // Remove duplicates and sort by probability
    const uniqueDiagnoses = this.removeDuplicateDiagnoses(diagnoses);
    
    return uniqueDiagnoses
      .sort((a, b) => b.probability - a.probability)
      .slice(0, 20); // Return top 20 most likely diagnoses
  }

  /**
   * Create diagnosis from ICD-11 entry
   */
  private static async createDiagnosisFromICD11(
    icdEntry: ICD11Entry,
    clinicalData: any,
    medicalKnowledge: MedicalKnowledgeEntry[]
  ): Promise<EnhancedDiagnosis> {
    // Find supporting research for this condition
    const supportingStudies = medicalKnowledge.filter(study => 
      study.title.toLowerCase().includes(icdEntry.title.toLowerCase().split(' ')[0]) ||
      study.condition_tags?.some(tag => 
        tag.toLowerCase().includes(icdEntry.title.toLowerCase().split(' ')[0])
      )
    );

    // Calculate probability based on symptom matching and severity
    const symptomMatches = clinicalData.symptoms.filter((symptom: string) =>
      icdEntry.symptom_mappings?.some(mapping => 
        mapping.toLowerCase().includes(symptom.toLowerCase()) ||
        symptom.toLowerCase().includes(mapping.toLowerCase())
      )
    ).length;

    let probability = 40; // Base probability
    probability += symptomMatches * 15; // +15 per matching symptom
    probability += this.getSeverityBonus(icdEntry.severity_level, clinicalData.severity);
    
    // Impact-based adjustment
    if (clinicalData.dailyImpact >= 8 && icdEntry.severity_level === 'high') probability += 20;
    else if (clinicalData.dailyImpact >= 6 && icdEntry.severity_level === 'medium') probability += 15;
    
    probability = Math.min(95, Math.max(10, probability));

    return {
      condition: icdEntry.title,
      icdCode: icdEntry.icd_code,
      probability,
      severity: this.mapSeverityLevel(icdEntry.severity_level),
      reasoning: this.generateReasoningForCondition(icdEntry, clinicalData, symptomMatches),
      clinicalPearls: this.generateClinicalPearls(icdEntry.title),
      requiredTests: this.generateRequiredTests(icdEntry.title, icdEntry.body_system),
      laymanDescription: icdEntry.definition || this.generateLaymanDescription(icdEntry.title),
      evidenceLevel: supportingStudies.length > 5 ? 'high' : supportingStudies.length > 2 ? 'medium' : 'low',
      supportingStudies: supportingStudies.slice(0, 3).map(s => s.title),
      urgencyLevel: this.determineUrgencyLevel(icdEntry, clinicalData),
      prevalence: this.getPrevalenceInfo(icdEntry.title),
      riskFactors: this.getRiskFactors(icdEntry.title)
    };
  }

  /**
   * Extract conditions from medical research
   */
  private static extractConditionsFromResearch(
    research: MedicalKnowledgeEntry[],
    clinicalData: any
  ): EnhancedDiagnosis[] {
    const diagnoses: EnhancedDiagnosis[] = [];

    // Group research by condition tags
    const conditionGroups = new Map<string, MedicalKnowledgeEntry[]>();
    
    research.forEach(study => {
      if (study.condition_tags) {
        study.condition_tags.forEach(condition => {
          if (!conditionGroups.has(condition)) {
            conditionGroups.set(condition, []);
          }
          conditionGroups.get(condition)!.push(study);
        });
      }
      
      // Also group by extracted conditions from title
      const extractedConditions = this.extractConditionsFromTitle(study.title);
      extractedConditions.forEach(condition => {
        if (!conditionGroups.has(condition)) {
          conditionGroups.set(condition, []);
        }
        conditionGroups.get(condition)!.push(study);
      });
    });

    // Create diagnoses from condition groups
    conditionGroups.forEach((studies, condition) => {
      if (studies.length >= 2 && condition.length > 3) { // Filter out single studies and short terms
        const probability = Math.min(85, 30 + (studies.length * 5));
        
        diagnoses.push({
          condition: condition,
          probability,
          severity: this.estimateSeverityFromStudies(studies),
          reasoning: `Condition identified from ${studies.length} medical studies matching reported symptoms`,
          evidenceLevel: studies.filter(s => s.evidence_level === 'high').length > 0 ? 'high' : 'medium',
          supportingStudies: studies.slice(0, 3).map(s => s.title),
          urgencyLevel: this.estimateUrgencyFromStudies(studies, clinicalData),
          laymanDescription: this.generateLaymanDescription(condition),
          clinicalPearls: this.generateClinicalPearls(condition),
          requiredTests: this.generateRequiredTests(condition)
        });
      }
    });

    return diagnoses;
  }

  /**
   * Get common conditions for category when database coverage is insufficient
   */
  private static async getCommonConditionsForCategory(
    category: string,
    clinicalData: any
  ): Promise<EnhancedDiagnosis[]> {
    // This provides backup coverage when database searches return insufficient results
    const commonConditions = this.getCommonConditionsByCategory(category);
    
    return commonConditions.map(condition => ({
      condition: condition.condition || 'Unknown condition',
      probability: Math.max(20, (condition.probability || 50) - 10), // Lower probability for fallback
      severity: condition.severity || 'mild',
      reasoning: `Analysis based on clinical data and symptom patterns`,
      evidenceLevel: 'medium' as const,
      supportingStudies: [`Clinical experience with ${category} conditions`],
      urgencyLevel: this.determineUrgencyFromSeverity(condition.severity || 'mild', clinicalData.dailyImpact),
      laymanDescription: this.generateLaymanDescription(condition.condition || 'Unknown condition'),
      clinicalPearls: this.generateClinicalPearls(condition.condition || 'Unknown condition'),
      requiredTests: this.generateRequiredTests(condition.condition || 'Unknown condition')
    }));
  }

  // Helper methods for condition analysis

  private static mapCategoryToBodySystem(category: string): string {
    const mapping: Record<string, string> = {
      'Chest and Breathing': 'chest/breathing',
      'Muscle and Bones': 'muscle/bones',
      'Abdomen and Bowels': 'stomach/digestion',
      'Head and Nerves': 'head/neurological',
      'Mental Health': 'mental health',
      'Heart and Blood': 'cardiovascular',
      'Skin': 'skin'
    };
    return mapping[category] || 'general';
  }

  private static getSeverityBonus(icdSeverity: string | undefined, reportedSeverity: number): number {
    if (!icdSeverity) return 0;
    
    if (icdSeverity === 'high' && reportedSeverity >= 7) return 15;
    if (icdSeverity === 'medium' && reportedSeverity >= 5) return 10;
    if (icdSeverity === 'low' && reportedSeverity <= 4) return 5;
    
    return 0;
  }

  private static mapSeverityLevel(severity: string | undefined): 'mild' | 'moderate' | 'severe' {
    switch (severity) {
      case 'high': return 'severe';
      case 'medium': return 'moderate';
      default: return 'mild';
    }
  }

  private static generateReasoningForCondition(
    icdEntry: ICD11Entry,
    clinicalData: any,
    symptomMatches: number
  ): string {
    const reasons = [];
    
    if (symptomMatches > 0) {
      reasons.push(`${symptomMatches} reported symptoms match this condition`);
    }
    
    if (icdEntry.severity_level && clinicalData.dailyImpact >= 7) {
      reasons.push(`Severity level (${icdEntry.severity_level}) consistent with reported impact`);
    }
    
    if (icdEntry.body_system) {
      reasons.push(`Anatomical system (${icdEntry.body_system}) aligns with symptom presentation`);
    }
    
    reasons.push(`WHO ICD-11 classification provides standardized diagnostic criteria`);
    
    return reasons.join('. ') + '.';
  }

  private static generateClinicalPearls(condition: string): string[] {
    // This could be expanded with a comprehensive database of clinical pearls
    const pearls: Record<string, string[]> = {
      'tension': ['Often stress-related', 'Responds to relaxation techniques', 'Caffeine withdrawal can trigger'],
      'migraine': ['Unilateral in 60% of cases', 'May have aura', 'Triggers include stress, foods, hormones'],
      'gastroenteritis': ['Usually self-limiting', 'Hydration is key', 'Consider C. diff if recent antibiotics'],
      'pneumonia': ['Consider atypical organisms in young adults', 'Chest X-ray may lag clinical symptoms'],
      'depression': ['Screen for suicide risk', 'Consider medical causes', 'Family history important'],
      'anxiety': ['Physical symptoms are real', 'Often comorbid with depression', 'CBT is first-line']
    };
    
    const conditionLower = condition.toLowerCase();
    for (const [key, value] of Object.entries(pearls)) {
      if (conditionLower.includes(key)) {
        return value;
      }
    }
    
    return ['Clinical correlation required', 'Consider differential diagnoses', 'Follow evidence-based guidelines'];
  }

  private static generateRequiredTests(
    condition: string,
    bodySystem?: string
  ): string[] {
    const conditionLower = condition.toLowerCase();
    
    // Condition-specific tests
    if (conditionLower.includes('cardiac') || conditionLower.includes('heart')) {
      return ['ECG', 'Troponin', 'Chest X-ray', 'Echocardiogram'];
    }
    
    if (conditionLower.includes('respiratory') || conditionLower.includes('pneumonia')) {
      return ['Chest X-ray', 'Full blood count', 'CRP', 'Sputum culture'];
    }
    
    if (conditionLower.includes('gastro') || conditionLower.includes('bowel')) {
      return ['Stool culture', 'FBC', 'CRP', 'Electrolytes'];
    }
    
    // Body system-specific tests
    if (bodySystem === 'respiratory') {
      return ['Chest X-ray', 'Peak flow', 'Pulse oximetry'];
    }
    
    if (bodySystem === 'musculoskeletal') {
      return ['X-ray', 'ESR', 'CRP', 'FBC'];
    }
    
    return ['Clinical assessment', 'Basic investigations as indicated', 'Specialist referral if needed'];
  }

  private static generateLaymanDescription(condition: string): string {
    // Enhanced descriptions for common conditions
    const descriptions: Record<string, string> = {
      'tension headache': 'A tension headache feels like a tight band or pressure around your head, often with mild to moderate pain in your forehead, temples, or back of the neck. It\'s commonly triggered by stress, poor posture, or lack of sleep and usually doesn\'t cause nausea or sensitivity to light like migraines do.',
      'migraine': 'A migraine is a type of headache that can cause severe throbbing pain, usually on one side of the head, often with nausea and sensitivity to light and sound. It may last hours to days and can be preceded by warning signs called aura.',
      'gastroenteritis': 'Gastroenteritis is inflammation of the stomach and intestines, commonly called stomach flu. It causes symptoms like nausea, vomiting, diarrhea, and stomach cramps, usually due to viral or bacterial infection.',
      'pneumonia': 'Pneumonia is an infection that inflames air sacs in one or both lungs, which may fill with fluid causing cough, fever, and difficulty breathing. It can be bacterial, viral, or fungal.',
      'anxiety disorder': 'An anxiety disorder is a mental health condition characterized by excessive worry, fear, or nervousness that interferes with daily activities. Physical symptoms may include rapid heartbeat, sweating, and restlessness.'
    };
    
    const conditionLower = condition.toLowerCase();
    for (const [key, description] of Object.entries(descriptions)) {
      if (conditionLower.includes(key.split(' ')[0])) {
        return description;
      }
    }
    
    return `${condition} is a medical condition that requires professional evaluation and diagnosis. Your healthcare provider can explain this condition in detail and discuss appropriate treatment options.`;
  }

  private static determineUrgencyLevel(
    icdEntry: ICD11Entry,
    clinicalData: any
  ): 'routine' | 'urgent' | 'immediate' {
    if (clinicalData.emergencyScreening) return 'immediate';
    
    if (icdEntry.severity_level === 'high' && clinicalData.dailyImpact >= 8) return 'urgent';
    if (clinicalData.dailyImpact >= 9) return 'urgent';
    if (icdEntry.severity_level === 'high') return 'urgent';
    
    return 'routine';
  }

  private static getPrevalenceInfo(condition: string): string {
    // This could be expanded with actual prevalence data
    const prevalence: Record<string, string> = {
      'tension headache': 'Very common (affects up to 80% of adults)',
      'migraine': 'Common (affects 12% of population)',
      'gastroenteritis': 'Very common (most people experience multiple episodes)',
      'anxiety': 'Common (affects 18% of adults annually)',
      'depression': 'Common (affects 8% of adults annually)'
    };
    
    const conditionLower = condition.toLowerCase();
    for (const [key, value] of Object.entries(prevalence)) {
      if (conditionLower.includes(key.split(' ')[0])) {
        return value;
      }
    }
    
    return 'Prevalence varies by population and risk factors';
  }

  private static getRiskFactors(condition: string): string[] {
    const riskFactors: Record<string, string[]> = {
      'tension headache': ['Stress', 'Poor posture', 'Caffeine withdrawal', 'Sleep deprivation'],
      'migraine': ['Family history', 'Hormonal changes', 'Certain foods', 'Stress'],
      'gastroenteritis': ['Contaminated food/water', 'Poor hygiene', 'Close contact with infected persons'],
      'pneumonia': ['Age >65', 'Smoking', 'Chronic lung disease', 'Immunocompromised'],
      'depression': ['Family history', 'Major life stressors', 'Chronic illness', 'Substance abuse']
    };
    
    const conditionLower = condition.toLowerCase();
    for (const [key, value] of Object.entries(riskFactors)) {
      if (conditionLower.includes(key.split(' ')[0])) {
        return value;
      }
    }
    
    return ['Variable risk factors', 'Consult healthcare provider for personalized assessment'];
  }

  // Additional helper methods

  private static extractConditionsFromTitle(title: string): string[] {
    // Extract potential condition names from research titles
    const conditions = [];
    const words = title.toLowerCase().split(' ');
    
    // Look for medical condition patterns
    const conditionKeywords = ['syndrome', 'disease', 'disorder', 'infection', 'inflammation', 'cancer', 'carcinoma', 'pneumonia', 'bronchitis', 'gastritis', 'arthritis', 'dermatitis'];
    
    for (let i = 0; i < words.length - 1; i++) {
      if (conditionKeywords.some(keyword => words[i + 1] === keyword)) {
        conditions.push(`${words[i]} ${words[i + 1]}`);
      }
    }
    
    return conditions;
  }

  private static estimateSeverityFromStudies(studies: MedicalKnowledgeEntry[]): 'mild' | 'moderate' | 'severe' {
    const emergencyStudies = studies.filter(s => 
      s.content?.toLowerCase().includes('emergency') || 
      s.content?.toLowerCase().includes('urgent') ||
      s.title.toLowerCase().includes('acute')
    );
    
    if (emergencyStudies.length > 0) return 'severe';
    if (studies.length > 5) return 'moderate';
    return 'mild';
  }

  private static estimateUrgencyFromStudies(
    studies: MedicalKnowledgeEntry[],
    clinicalData: any
  ): 'routine' | 'urgent' | 'immediate' {
    const urgentStudies = studies.filter(s => 
      s.content?.toLowerCase().includes('emergency') || 
      s.content?.toLowerCase().includes('immediate')
    );
    
    if (urgentStudies.length > 0 && clinicalData.dailyImpact >= 8) return 'immediate';
    if (urgentStudies.length > 0 || clinicalData.dailyImpact >= 7) return 'urgent';
    return 'routine';
  }

  private static determineUrgencyFromSeverity(
    severity: 'mild' | 'moderate' | 'severe',
    dailyImpact: number
  ): 'routine' | 'urgent' | 'immediate' {
    if (severity === 'severe' && dailyImpact >= 8) return 'immediate';
    if (severity === 'severe' || dailyImpact >= 7) return 'urgent';
    return 'routine';
  }

  private static removeDuplicateDiagnoses(diagnoses: EnhancedDiagnosis[]): EnhancedDiagnosis[] {
    const seen = new Set<string>();
    return diagnoses.filter(diagnosis => {
      const key = diagnosis.condition.toLowerCase().trim();
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  private static generateClinicalReasoning(
    clinicalData: any,
    diagnoses: EnhancedDiagnosis[],
    icd11Results: any
  ): string[] {
    const reasoning = [];
    
    reasoning.push(`Clinical presentation: ${clinicalData.symptoms.length} primary symptoms reported in ${clinicalData.category} category`);
    
    if (clinicalData.emergencyScreening) {
      reasoning.push('Emergency screening positive - immediate evaluation pathway initiated');
    }
    
    reasoning.push(`Symptom impact: ${clinicalData.dailyImpact}/10 functional impairment reported`);
    
    if (clinicalData.duration) {
      reasoning.push(`Temporal pattern: ${clinicalData.duration} duration influences diagnostic consideration`);
    }
    
    reasoning.push(`Database analysis: ${icd11Results.primaryConditions.length} primary ICD-11 matches identified`);
    
    if (diagnoses.length > 0) {
      const topDiagnosis = diagnoses[0];
      reasoning.push(`Leading differential: ${topDiagnosis.condition} (${topDiagnosis.probability}% likelihood based on symptom matching and evidence)`);
      
      if (topDiagnosis.evidenceLevel === 'high') {
        reasoning.push(`Strong evidence base: ${topDiagnosis.supportingStudies.length} high-quality studies support this diagnostic consideration`);
      }
    }
    
    reasoning.push(`Comprehensive analysis: ${diagnoses.length} potential diagnoses considered from medical literature and ICD-11 database`);
    
    return reasoning;
  }

  private static assessEvidenceQuality(
    medicalKnowledge: MedicalKnowledgeEntry[],
    icd11Results: any,
    diagnoses: EnhancedDiagnosis[]
  ): 'high' | 'medium' | 'low' {
    const totalEvidence = medicalKnowledge.length + icd11Results.primaryConditions.length;
    const highQualityStudies = medicalKnowledge.filter(s => s.evidence_level === 'high').length;
    const diagnosesWithHighEvidence = diagnoses.filter(d => d.evidenceLevel === 'high').length;
    
    if (totalEvidence >= 20 && highQualityStudies >= 5 && diagnosesWithHighEvidence >= 2) {
      return 'high';
    }
    
    if (totalEvidence >= 10 && (highQualityStudies >= 2 || diagnosesWithHighEvidence >= 1)) {
      return 'medium';
    }
    
    return 'low';
  }

  private static getCommonConditionsByCategory(category: string): Partial<EnhancedDiagnosis>[] {
    const conditions: Record<string, Partial<EnhancedDiagnosis>[]> = {
      'Muscle and Bones': [
        { condition: 'Musculoskeletal strain', probability: 65, severity: 'mild' },
        { condition: 'Osteoarthritis', probability: 45, severity: 'moderate' },
        { condition: 'Rheumatoid arthritis', probability: 35, severity: 'moderate' },
        { condition: 'Fibromyalgia', probability: 40, severity: 'moderate' },
        { condition: 'Tendonitis', probability: 50, severity: 'mild' }
      ],
      'Chest and Breathing': [
        { condition: 'Upper respiratory infection', probability: 70, severity: 'mild' },
        { condition: 'Asthma exacerbation', probability: 55, severity: 'moderate' },
        { condition: 'Bronchitis', probability: 60, severity: 'moderate' },
        { condition: 'Pneumonia', probability: 40, severity: 'severe' },
        { condition: 'Chest wall pain', probability: 45, severity: 'mild' }
      ],
      'Abdomen and Bowels': [
        { condition: 'Gastroenteritis', probability: 75, severity: 'moderate' },
        { condition: 'Irritable bowel syndrome', probability: 50, severity: 'mild' },
        { condition: 'Gastroesophageal reflux disease', probability: 60, severity: 'mild' },
        { condition: 'Peptic ulcer disease', probability: 35, severity: 'moderate' },
        { condition: 'Inflammatory bowel disease', probability: 25, severity: 'severe' }
      ],
      'Head and Nerves': [
        { condition: 'Tension headache', probability: 70, severity: 'mild' },
        { condition: 'Migraine', probability: 55, severity: 'moderate' },
        { condition: 'Cluster headache', probability: 25, severity: 'severe' },
        { condition: 'Sinusitis', probability: 45, severity: 'mild' },
        { condition: 'Cervicogenic headache', probability: 35, severity: 'moderate' }
      ],
      'Mental Health': [
        { condition: 'Generalized anxiety disorder', probability: 65, severity: 'moderate' },
        { condition: 'Major depressive disorder', probability: 55, severity: 'moderate' },
        { condition: 'Panic disorder', probability: 45, severity: 'moderate' },
        { condition: 'Adjustment disorder', probability: 50, severity: 'mild' },
        { condition: 'Bipolar disorder', probability: 30, severity: 'severe' }
      ],
      'Heart and Blood': [
        { condition: 'Hypertension', probability: 60, severity: 'moderate' },
        { condition: 'Coronary artery disease', probability: 40, severity: 'severe' },
        { condition: 'Atrial fibrillation', probability: 35, severity: 'moderate' },
        { condition: 'Heart failure', probability: 30, severity: 'severe' },
        { condition: 'Anemia', probability: 45, severity: 'mild' }
      ]
    };
    
    return conditions[category] || [
      { condition: 'General medical condition', probability: 50, severity: 'mild' }
    ];
  }

  private static generateFallbackDiagnosis(answers: Record<string, any>): AdvancedDiagnosticResult {
    const category = answers.symptom_constellation?.category || 'General Assessment';
    
    return {
      potentialDiagnoses: [{
        condition: `${category} - Clinical Evaluation Required`,
        probability: 75,
        severity: 'mild',
        reasoning: 'Comprehensive clinical assessment needed for accurate diagnosis',
        evidenceLevel: 'medium',
        supportingStudies: ['Clinical practice guidelines'],
        urgencyLevel: 'routine',
        laymanDescription: 'Your symptoms require professional medical evaluation to determine the exact cause and appropriate treatment.',
        clinicalPearls: ['Systematic evaluation recommended', 'Consider common conditions first'],
        requiredTests: ['Clinical examination', 'Basic investigations as indicated']
      }],
      clinicalReasoningPath: [
        `Patient presents with symptoms in ${category} category`,
        'Systematic clinical evaluation recommended',
        'Evidence-based diagnostic approach should be employed'
      ],
      evidenceQuality: 'medium',
      totalConditionsConsidered: 1,
      databaseCoverage: {
        icd11Matches: 0,
        researchPapers: 0,
        clinicalGuidelines: 1
      }
    };
  }
}